import { useState, useRef, useEffect, useCallback, FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useSearch } from "../../hooks/useSearch";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Product } from "../../pages/Home/Home";

type SearchMode = "reference" | "title";

const SEARCH_BY_PARAM = "searchBy";

function modeFromParams(params: URLSearchParams): SearchMode {
  return params.get(SEARCH_BY_PARAM) === "reference" ? "reference" : "title";
}

function SearchBox({
  setSearchProducts,
  category,
  onNewSearch,
}: {
  setSearchProducts: (products: number[] | null) => void;
  category: number | undefined;
  onNewSearch?: () => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") || "";
  const [inputValue, setInputValue] = useState(initialSearch);
  const [searchMode, setSearchMode] = useState<SearchMode>(() =>
    modeFromParams(searchParams)
  );
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchClient = useSearch();

  const handleSearch = useCallback(
    async (searchQuery: string, mode: SearchMode) => {
      if (searchQuery.length === 0) {
        return;
      }

      setIsSearching(true);
      try {
        const index = searchClient.index("stock");
        const categoryFilter = category
          ? `Categories = ${category}`
          : undefined;
        const trimmedQuery = searchQuery.trim();

        if (mode === "reference") {
          if (!/^\d+$/.test(trimmedQuery)) {
            setSearchProducts([]);
            return;
          }

          const referenceFilter = categoryFilter
            ? `Reference = ${trimmedQuery} AND ${categoryFilter}`
            : `Reference = ${trimmedQuery}`;

          const referenceResults = await index.search("", {
            limit: 1,
            filter: referenceFilter,
          });

          setSearchProducts(
            (referenceResults.hits as Product[]).map((result) => result.ID)
          );
          return;
        }

        const searchResults = await index.search(trimmedQuery, {
          limit: 100,
          ...(categoryFilter ? { filter: categoryFilter } : {}),
        });

        const results = searchResults.hits as Product[];
        setSearchProducts(results.map((result) => result.ID));
      } catch (error) {
        console.error("Search error:", error);
        setSearchProducts(null);
      } finally {
        setIsSearching(false);
      }
    },
    [category, searchClient, setSearchProducts]
  );

  // Re-run search from URL param on mount (e.g. when navigating back)
  useEffect(() => {
    if (initialSearch) {
      handleSearch(initialSearch, modeFromParams(searchParams));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const writeSearchParams = (query: string, mode: SearchMode) => {
    const nextParams = new URLSearchParams(searchParams);
    if (query) {
      nextParams.set("search", query);
    } else {
      nextParams.delete("search");
    }
    if (mode === "reference") {
      nextParams.set(SEARCH_BY_PARAM, "reference");
    } else {
      nextParams.delete(SEARCH_BY_PARAM);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (inputRef.current) {
      inputRef.current.blur();
    }

    // Clear saved scroll position for fresh searches
    onNewSearch?.();
    writeSearchParams(inputValue, searchMode);
    handleSearch(inputValue, searchMode);
  };

  const handleModeChange = (mode: SearchMode) => {
    setSearchMode(mode);
    writeSearchParams(inputValue, mode);

    if (inputValue.trim().length > 0) {
      onNewSearch?.();
      handleSearch(inputValue, mode);
    }
  };

  const handleReset = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setInputValue("");
    setSearchProducts(null);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("search");
    setSearchParams(nextParams, { replace: true });

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      onReset={handleReset}
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "center",
        flexWrap: "wrap",
        width: "100%",
        maxWidth: 760,
      }}
    >
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <Select
          value={searchMode}
          onChange={(event) =>
            handleModeChange(event.target.value as SearchMode)
          }
          inputProps={{ "aria-label": "Search by" }}
        >
          <MenuItem value="reference">by reference</MenuItem>
          <MenuItem value="title">by title</MenuItem>
        </Select>
      </FormControl>
      <TextField
        inputRef={inputRef}
        sx={{ flex: "1 1 220px" }}
        variant="outlined"
        size="small"
        placeholder={
          searchMode === "reference" ? "Reference number" : "Search..."
        }
        type="search"
        value={inputValue}
        onChange={(event) => {
          setInputValue(event.target.value);
        }}
        InputProps={{
          endAdornment: isSearching && <CircularProgress size={20} />,
        }}
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={inputValue.length === 0}
      >
        Search
      </Button>
      {inputValue.length > 0 && !isSearching && (
        <Button type="reset" variant="outlined">
          Clear
        </Button>
      )}
    </Box>
  );
}

export default function Search({
  setSearchProducts,
  category,
  onNewSearch,
}: {
  setSearchProducts: (products: number[] | null) => void;
  category: number | undefined;
  onNewSearch?: () => void;
}) {
  return (
    <SearchBox
      setSearchProducts={setSearchProducts}
      category={category}
      onNewSearch={onNewSearch}
    />
  );
}
